import type { FeatureContext } from './featureContext';
import { initStormBoard } from '../ui/eventBoard';
import { createActiveEdge, stormCue } from '../audio/defineCue';
import { vortexCreatureCue } from '../audio/combatCue';
import { isVortexBody } from '../sim/content';
import { proximityCueVolume } from './cueVolume';

// Loudness ceiling for an energy vortex's wind-gust swing at distance 0 (Blackthorn's
// Storm Slice E, ADR-0152), proximity-scaled per swing like a rat squeal - a vortex
// bites all over the bank, so a distant swing fades out. Mixed like the rat squeal.
const VORTEX_ATTACK_VOLUME = 0.7;

/** Injectable seams for unit tests; defaults are the real modules. */
export interface StormInstallDeps {
  initStormBoard?: typeof initStormBoard;
}

/**
 * The Blackthorn's Storm presentation (PRD #289): the live storm board (Slice F,
 * ADR-0153), the storm-boundary audio cues + the dungeon2 music override
 * (ADR-0152/0156), the ground-summon telegraph sparkle, the vortex-kind swing
 * gusts, and the storm-end corpse sweep.
 *
 * The one `stormStatus` handler deliberately keeps its **three consumers
 * together** (the swarm-status precedent): the board mirrors the latest push; the
 * boundary cue hangs off the active<->inactive edge - Blackthorn's arrival growl
 * on the `-> active` edge, the mass-resurrection chime on the `-> inactive` edge
 * (the storm breaking and the fallen rising), each for everyone in the bank; and
 * the storm music override (dungeon2) engages/disengages on the same edge. Same
 * edge discipline as the swarm: seed silently on the first push, act only on a
 * real transition (a mid-storm login hears the growl and starts the dirge once).
 */
export function installStorm(ctx: FeatureContext, deps: StormInstallDeps = {}): void {
  const stormBoard = (deps.initStormBoard ?? initStormBoard)();

  // The storm's boundary edge latch (the shared discipline, src/audio/defineCue.ts):
  // re-seeded on each connection, with the same mid-event-login-hears-it-once
  // trade-off as the swarm (Slice E, ADR-0152).
  const stormEdge = createActiveEdge();
  ctx.events.on('connection', () => stormEdge.reset());

  // Reset the storm board on every (re)connection (the swarm board's reconnect
  // guard, ADR-0153): the shared handle outlives the socket, the board only hides
  // on an `active:false` push, and the server only seeds a StormStatus at login
  // while a storm is running - so a reconnect spanning the storm's end would
  // otherwise strand it on screen. If a storm is genuinely still running, the
  // mid-storm seed push repaints it in the same login flush.
  ctx.events.on('welcome', () => stormBoard.reset());

  ctx.events.on('stormStatus', (msg) => {
    stormBoard.update(msg);
    if (stormEdge.observe(msg.active)) {
      ctx.audio.play(stormCue(msg.active));
      ctx.music.setStorm(msg.active);
    }
  });

  // The storm spawn telegraph (Slice D, ADR-0151): an ephemeral one-shot warning that
  // a vortex will materialize on a ground tile in ~`ticksUntil` ticks. Render the
  // 2-second sparkle there (Slice E, ADR-0152); `ticksUntil` drives its lifetime so it
  // winks out as the vortex lands. Ephemeral like the action broadcast - a client that
  // drops it just misses the warning, no state to catch up.
  ctx.events.on('stormTelegraph', (msg) => {
    if (!ctx.ui.hidden) ctx.renderer()?.showStormTelegraph(msg.x, msg.y, msg.ticksUntil);
  });

  // A vortex-kind creature's attack swing (Slice E, ADR-0152 + the chaos-llama
  // variant, ADR-0162): a randomized wind gust for a normal energy vortex, a llama
  // bark for a llama, proximity-scaled to the local player like a rat squeal (they
  // roam and bite all over the bank). Gated on the source being a vortex-kind body
  // so a player/NPC attack never sounds wind; the monster attack clips are
  // `attack-a/-b/-c` (Slice B), any of which is the swing. `vortexCreatureCue`
  // picks the right set by body. Dropped when out of earshot.
  ctx.events.on('actionAnimation', (msg) => {
    const source = ctx.world()?.get(msg.entityId)?.renderable;
    if (isVortexBody(source?.body) && msg.clip.startsWith('attack-')) {
      const volume = proximityCueVolume(ctx.world(), ctx.playerId(), msg.entityId, VORTEX_ATTACK_VOLUME);
      const gust = vortexCreatureCue(source?.body, volume);
      if (gust) ctx.audio.play(gust);
    }
  });

  // Wipe every fallen body on the ground (corpse/ghost split, ADR-0140): the server
  // fires this at storm end and from the admin "Clear all bodies" control, so a
  // storm's mass of frozen corpses can't linger as a render cost.
  ctx.events.on('clearCorpses', () => ctx.renderer()?.clearAllCorpses());

  // Tick the board's countdown between pushes (Slice F, ADR-0153): a no-op while no
  // storm runs or during the pre-summon entrance (no clock yet).
  ctx.onFrame(() => stormBoard.sync());
}
